import type { DaycareSlot } from '~/stores/useDaycareStore'
import { useDaycareStore } from '~/stores/useDaycareStore'
import type { DaycareQueueEntry } from '~/utils/daycare'
import {
  createDaycareDepositId,
  hasDaycareDepositId,
  reconcileDaycareSlotStarsAndDamage,
} from '../../../shared/daycare_merge'
import { normalizeDaycareRevision } from '../../../shared/daycare_revision'
import { isDaycareExplicitlyCleared } from '../../../shared/daycare_merge'

function clampDaycareStars(value: unknown): number {
  const n = Math.floor(Number(value) || 1)
  return Math.max(1, Math.min(5, n))
}

export function mapServerDaycareSlot(raw: Record<string, unknown>): DaycareSlot {
  const slot = reconcileDaycareSlotStarsAndDamage({
    ...raw,
    slug: String(raw.slug ?? ''),
    nameFr: String(raw.nameFr ?? raw.slug ?? ''),
    nameEn: String(raw.nameEn ?? raw.slug ?? ''),
    rarity: (raw.rarity as DaycareSlot['rarity']) ?? 'common',
    stars: clampDaycareStars(raw.stars),
    isShiny: Boolean(raw.isShiny),
    damageDealt: Math.max(0, Number(raw.damageDealt) || 0),
    damageRequired: Math.max(0, Number(raw.damageRequired) || 0),
    cosmeticFormSlug: (raw.cosmeticFormSlug as string | null) ?? null,
    loopSameSpecies: raw.loopSameSpecies !== false,
  }) as DaycareSlot
  if (!hasDaycareDepositId(slot)) {
    return { ...slot, depositId: createDaycareDepositId() }
  }
  return slot
}

function mapServerQueueEntry(raw: Record<string, unknown>): DaycareQueueEntry {
  return {
    slug: String(raw.slug ?? ''),
    nameFr: String(raw.nameFr ?? raw.slug ?? ''),
    nameEn: String(raw.nameEn ?? raw.slug ?? ''),
    rarity: (raw.rarity as DaycareQueueEntry['rarity']) ?? 'common',
    isShiny: Boolean(raw.isShiny),
    stars: clampDaycareStars(raw.stars),
    cosmeticFormSlug: (raw.cosmeticFormSlug as string | null) ?? null,
  }
}

export type ServerDaycarePayload = {
  revision?: number
  slots?: unknown[]
  autoRefill?: boolean
  autoVacancyFill?: boolean
  lastDeposited?: unknown[]
  reserveQueue?: unknown[]
  reserveFillPending?: number
}

/** Applique l’état serveur dans le store (source de vérité au load / conflit 409). */
export function applyServerDaycareToStore(
  raw: ServerDaycarePayload | unknown[] | null | undefined,
  maxSlots: number
): number {
  const daycare = useDaycareStore()
  const legacyArray = Array.isArray(raw)

  if (legacyArray) {
    const slots = raw.map((s) => mapServerDaycareSlot(s as Record<string, unknown>)).slice(0, maxSlots)
    daycare.setSlots(slots)
    daycare.setLastDeposited([])
    daycare.setReserveQueue([])
    daycare.setReserveFillPending(0)
    daycare.setAutoVacancyFill(true)
    const rev = normalizeDaycareRevision(0)
    daycare.setServerRevision(rev)
    daycare.clearPendingRevision()
    return rev
  }

  const o = raw && typeof raw === 'object' ? (raw as ServerDaycarePayload) : null
  if (!o) {
    daycare.setSlots([])
    daycare.setLastDeposited([])
    daycare.setReserveQueue([])
    daycare.setReserveFillPending(0)
    const rev = normalizeDaycareRevision(0)
    daycare.setServerRevision(rev)
    daycare.clearPendingRevision()
    return rev
  }

  const slots = Array.isArray(o.slots)
    ? o.slots.map((s) => mapServerDaycareSlot(s as Record<string, unknown>)).slice(0, maxSlots)
    : []
  daycare.setSlots(slots)
  daycare.setLastDeposited(
    Array.isArray(o.lastDeposited)
      ? o.lastDeposited.map((d) => mapServerQueueEntry(d as Record<string, unknown>))
      : []
  )
  daycare.setReserveQueue(
    Array.isArray(o.reserveQueue)
      ? o.reserveQueue.map((d) => mapServerQueueEntry(d as Record<string, unknown>))
      : []
  )
  daycare.setReserveFillPending(Math.max(0, Math.floor(Number(o.reserveFillPending) || 0)))
  daycare.setAutoRefill(Boolean(o.autoRefill))
  daycare.setAutoVacancyFill(o.autoVacancyFill !== false)

  const rev = normalizeDaycareRevision(o.revision)
  daycare.setServerRevision(rev)
  daycare.clearPendingRevision()
  return rev
}

export function isServerDaycarePayloadEmpty(raw: ServerDaycarePayload | unknown[] | null | undefined): boolean {
  if (Array.isArray(raw)) return raw.length === 0
  if (!raw || typeof raw !== 'object') return true
  return isDaycareExplicitlyCleared({
    slots: Array.isArray(raw.slots) ? raw.slots : [],
    lastDeposited: Array.isArray(raw.lastDeposited) ? raw.lastDeposited : [],
    reserveQueue: Array.isArray(raw.reserveQueue) ? raw.reserveQueue : [],
  })
}
